import React from 'react';
import { Link } from 'react-router-dom';
import { motion } from 'framer-motion';
import { Instagram, Facebook, Youtube, Mail, Phone, MapPin, ExternalLink } from 'lucide-react';
import { useSiteConfig } from '../context/SiteConfigContext';

const Footer = () => {
  const siteConfig = useSiteConfig();
  const quickLinks = [
    { path: '/', label: 'Accueil' },
    { path: '/quest-ce-que-la-gr', label: 'La GR' },
    { path: '/notre-club', label: 'Notre Club' },
    { path: '/inscriptions', label: 'Inscriptions' },
  ];

  const externalLinks = [
    { href: siteConfig.socialLinks.monClub, label: 'Mon Club', icon: ExternalLink },
    { href: siteConfig.socialLinks.boutique, label: 'Boutique', icon: ExternalLink },
  ];

  return (
    <footer className="relative bg-black text-white overflow-hidden">
      {/* Gradient Overlay */}
      <div className="absolute inset-0 bg-gradient-to-b from-transparent via-fuchsia-950/10 to-black pointer-events-none" />
      
      <div className="container mx-auto px-6 py-16 relative z-10">
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-12 mb-12">
          {/* Club Info */}
          <div>
            <motion.h3
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              className="text-2xl font-bold bg-gradient-to-r from-fuchsia-400 to-pink-400 bg-clip-text text-transparent mb-4"
            >
              {siteConfig.clubName}
            </motion.h3>
            <p className="text-white/60 text-sm leading-relaxed mb-6">
              Club de Gymnastique Rythmique à La Réunion. Excellence, passion et élégance depuis 1995.
            </p>
            <div className="flex gap-4">
              <motion.a
                href={siteConfig.socialLinks.facebook}
                target="_blank"
                rel="noopener noreferrer"
                whileHover={{ scale: 1.1, y: -2 }}
                className="w-10 h-10 rounded-full bg-white/5 hover:bg-fuchsia-500/20 border border-white/10 hover:border-fuchsia-500/50 flex items-center justify-center text-white/60 hover:text-fuchsia-400 transition-all duration-300"
              >
                <Facebook size={18} />
              </motion.a>
              <motion.a
                href={siteConfig.socialLinks.instagram}
                target="_blank"
                rel="noopener noreferrer"
                whileHover={{ scale: 1.1, y: -2 }}
                className="w-10 h-10 rounded-full bg-white/5 hover:bg-fuchsia-500/20 border border-white/10 hover:border-fuchsia-500/50 flex items-center justify-center text-white/60 hover:text-fuchsia-400 transition-all duration-300"
              >
                <Instagram size={18} />
              </motion.a>
              <motion.a
                href={siteConfig.socialLinks.youtube}
                target="_blank"
                rel="noopener noreferrer"
                whileHover={{ scale: 1.1, y: -2 }}
                className="w-10 h-10 rounded-full bg-white/5 hover:bg-fuchsia-500/20 border border-white/10 hover:border-fuchsia-500/50 flex items-center justify-center text-white/60 hover:text-fuchsia-400 transition-all duration-300"
              >
                <Youtube size={18} />
              </motion.a>
            </div>
          </div>

          {/* Quick Links */}
          <div>
            <h4 className="text-lg font-semibold mb-6 text-white">Navigation</h4>
            <ul className="space-y-3">
              {quickLinks.map((link) => (
                <li key={link.path}>
                  <Link
                    to={link.path}
                    className="text-white/60 hover:text-fuchsia-400 transition-colors duration-300 text-sm inline-block hover:translate-x-1 transform"
                  >
                    {link.label}
                  </Link>
                </li>
              ))}
            </ul>
          </div>

          {/* External Links */}
          <div>
            <h4 className="text-lg font-semibold mb-6 text-white">Liens Utiles</h4>
            <ul className="space-y-3">
              {externalLinks.map((link) => (
                <li key={link.href}>
                  <a
                    href={link.href}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="text-white/60 hover:text-fuchsia-400 transition-colors duration-300 text-sm inline-flex items-center gap-2 hover:translate-x-1 transform"
                  >
                    {link.label}
                    <link.icon size={14} />
                  </a>
                </li>
              ))}
              <li>
                <Link
                  to="/resultats"
                  className="text-white/60 hover:text-fuchsia-400 transition-colors duration-300 text-sm inline-block hover:translate-x-1 transform"
                >
                  Résultats & Galas
                </Link>
              </li>
            </ul>
          </div>

          {/* Contact */}
          <div>
            <h4 className="text-lg font-semibold mb-6 text-white">Contact</h4>
            <ul className="space-y-4">
              <li className="flex items-start gap-3 text-sm">
                <Phone size={16} className="text-fuchsia-400 mt-1 flex-shrink-0" />
                <span className="text-white/60">{siteConfig.contact.phone}</span>
              </li>
              <li className="flex items-start gap-3 text-sm">
                <Mail size={16} className="text-fuchsia-400 mt-1 flex-shrink-0" />
                <a href={`mailto:${siteConfig.contact.email}`} className="text-white/60 hover:text-fuchsia-400 transition-colors">
                  {siteConfig.contact.email}
                </a>
              </li>
              <li className="flex items-start gap-3 text-sm">
                <MapPin size={16} className="text-fuchsia-400 mt-1 flex-shrink-0" />
                <span className="text-white/60">{siteConfig.contact.address}</span>
              </li>
            </ul>
          </div>
        </div>

        {/* Bottom Bar */}
        <div className="pt-8 border-t border-white/10">
          {siteConfig.credits && (
            <p className="text-center text-white/50 text-sm mb-4 whitespace-pre-line" data-testid="footer-credits">
              {siteConfig.credits}
            </p>
          )}
          <div className="flex flex-col md:flex-row justify-between items-center gap-4 text-sm text-white/40">
            <p>
              © {new Date().getFullYear()} {siteConfig.clubName}. Tous droits réservés.
            </p>
            <div className="flex items-center gap-4">
              <p className="text-xs">
                Construit avec passion pour l'excellence • La Réunion 974
              </p>
              <Link
                to="/admin/login"
                data-testid="footer-admin-link"
                className="text-xs text-white/40 hover:text-fuchsia-400 transition-colors duration-300 border border-white/10 hover:border-fuchsia-500/40 rounded-full px-3 py-1"
              >
                Espace Gérant
              </Link>
            </div>
          </div>
        </div>
      </div>

      {/* Decorative Elements */}
      <div className="absolute bottom-0 left-0 w-96 h-96 bg-fuchsia-500/5 rounded-full blur-3xl" />
      <div className="absolute top-0 right-0 w-96 h-96 bg-pink-500/5 rounded-full blur-3xl" />
    </footer>
  );
};

export default Footer;
